import { useTranslation } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { SelectOption } from '@anytag/shared-types';
import { Select, SelectionBar } from '@anytag/shared-atoms';
import { useSelectItems, useToggleState } from '@anytag/shared-hooks';
import { EngagementPost, EngagementCampaignPostStatus, namedOperations } from '@anytag/graphql';
import { useUpdateEngagementPostsStatus } from './hooks';

interface ActionBarProps extends ReturnType<typeof useSelectItems<EngagementPost, keyof EngagementPost>> {
  isTtcmCampaign?: boolean;
}

export const ActionBar = ({
  selectedItems,
  isTtcmCampaign,
  selectedItemIds,
  setSelectedItemIds,
  hasAllItemsSelected,
  toggleSelectAllItems
}: ActionBarProps) => {
  const { t } = useTranslation();
  const loadingState = useToggleState();
  const { isAgency, isAdvertiser, isAdminStaffTalentAgencyPartner } = useUserRoles();
  const { handleSelectStatus, UpdateEngagementPostStatusModal } = useUpdateEngagementPostsStatus({
    refetchQueries: [namedOperations.Query.AllEngagementPosts]
  });

  const numberSelectedIds = selectedItemIds.length;
  const options: SelectOption<EngagementCampaignPostStatus>[] = [
    {
      label: t<string>('Option.Approved'),
      value: EngagementCampaignPostStatus.READY_TO_POST
    },
    {
      hidden: isTtcmCampaign,
      label: t<string>('Option.Warning'),
      value: EngagementCampaignPostStatus.WARNING
    },
    {
      label: t<string>('Option.Warning Solved'),
      value: EngagementCampaignPostStatus.WARNING_SOLVED,
      hidden: isTtcmCampaign || !(isAdminStaffTalentAgencyPartner || isAdvertiser || isAgency)
    },
    {
      label: t<string>('Option.Ineffective'),
      hidden: !isAdminStaffTalentAgencyPartner,
      value: EngagementCampaignPostStatus.INEFFECTIVE
    }
  ].filter((option) => !option.hidden);

  return numberSelectedIds > 0 ? (
    <>
      <SelectionBar
        disabled={loadingState.status}
        selectAllProps={{ onClick: toggleSelectAllItems }}
        checkboxProps={{
          disabled: loadingState.status,
          onClick: toggleSelectAllItems,
          indeterminate: !hasAllItemsSelected,
          label: t('Selected', { count: numberSelectedIds })
        }}
      >
        <Select
          options={options}
          clearable={false}
          className="ml-4 w-45"
          disabled={loadingState.status}
          onChange={(value) => handleSelectStatus(value, selectedItems)}
        />
      </SelectionBar>

      <UpdateEngagementPostStatusModal onSuccess={() => setSelectedItemIds([])} />
    </>
  ) : null;
};
